import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Carousel, Button } from 'antd';
import RSSParser from 'rss-parser';
import Layout from '../../components/Layout/Layout';
import { NewsContainer } from '../../components/newsContainer/NewsContainer';
import { getLandingConfig, getLandingCssVars } from './getLandingConfig';
import './Landing.scss';

const RSS_URL = 'https://lmigroup.io/feed/';

/** Design stacked heading: kicker (h2) + title (h1) read as one phrase. */
function StackedHeading({ heading }) {
  if (!heading) return null;
  return (
    <>
      <h2>{heading.kicker}</h2>
      <h1>{heading.title}</h1>
    </>
  );
}

function landingClassName(flags) {
  const classes = ['landing-container'];
  if (flags.sentenceCase) classes.push('landing-container--sentence-case');
  if (flags.unifiedBenefitAccent) classes.push('landing-container--unified-benefit-accent');
  return classes.join(' ');
}

function RequirementText({ copy, citeRequirement }) {
  const name = citeRequirement ? (
    <cite className="landing-requirement-name">{copy.whyRequirement}</cite>
  ) : (
    copy.whyRequirement
  );

  return (
    <p>
      ContinuityCoach.com is structured and is updated where appropriate to reflect current best business continuity
      management practice as stated in {name}
      {copy.whyRequirementAfter ? (
        <>
          <br />
          {copy.whyRequirementAfter}
        </>
      ) : citeRequirement ? (
        '.'
      ) : null}
    </p>
  );
}

// Fires `.in-view` on every `.reveal` element as it enters the viewport.
// Once triggered, the element is unobserved so the animation plays only once.
function useScrollReveal() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' },
    );

    document.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

export default function Landing() {
  const sliderRef = useRef(null);
  const sliderNewsRef = useRef(null);
  const landing = getLandingConfig();
  const { images, copy, flags } = landing;

  const [mounted, setMounted] = useState(false);
  const [innerWidth, setInnerWidth] = useState(window.innerWidth);
  const [newsList, setNewsList] = useState([]);

  useScrollReveal();

  // Defer the banner `.mounted` class one animation frame so the browser paints
  // the initial hidden state before the CSS keyframe animations start.
  useEffect(() => {
    const raf = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    // rss-parser's parseURL uses Node.js https/http which are not polyfilled in
    // Webpack 5 (react-scripts 5+). Use the browser's fetch + parseString instead.
    const loadNews = async () => {
      try {
        const response = await fetch(RSS_URL);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const xml = await response.text();
        const feed = await new RSSParser().parseString(xml);
        setNewsList(feed.items);
      } catch {
        // Network or CORS error — news section stays empty without breaking the page.
      }
    };
    loadNews();

    const onResize = () => setInnerWidth(window.innerWidth);
    window.addEventListener('resize', onResize, { passive: true });
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <Layout>
      <div className={landingClassName(flags)} style={getLandingCssVars(landing)}>
        {/* ── Hero Banner ───────────────────────────────────────────── */}
        <div className={`landing-banner${mounted ? ' mounted' : ''}`} style={{ backgroundImage: `url(${images.bannerBg})` }}>
          <div className="landing-banner-text">
            <h1>
              {copy.heroHeading.titleLines.map((line, index) => (
                <React.Fragment key={line}>
                  {index > 0 && <br />}
                  {line}
                </React.Fragment>
              ))}
            </h1>
            <h2>{copy.heroHeading.kicker}</h2>
            <p className="slogan">{copy.heroSlogan}</p>
          </div>
          <img className="steps" src={images.steps} alt="" />
          <img className="ring" src={images.ring} alt="" />
        </div>

        {/* ── Section 2 — What is BCM ───────────────────────────────── */}
        <Row type="flex" gutter={32} justify="center" align="middle" className="landing-section-two reveal">
          <Col xs={22} sm={20} md={20} lg={8} xl={8} xxl={6} className="landing-section-two-text">
            <StackedHeading heading={copy.bcmHeading} />
            <p>{copy.bcmLead}</p>
            <p>{copy.bcmClose}</p>
          </Col>
          <Col xs={22} sm={20} md={20} lg={12} xl={10} xxl={10}>
            <div className="responsive-video-container">
              <iframe
                title="intro"
                src={landing.videoSrc}
                frameBorder="0"
                allow="autoplay; encrypted-media"
                allowFullScreen
                className="responsive-video"
              />
            </div>
          </Col>
        </Row>

        {/* ── Section 3 — Why ContinuityCoach ──────────────────────── */}
        <Row type="flex" justify="center" className="landing-section-three reveal">
          <Col xs={20} sm={20} md={22} lg={20} xl={18} xxl={16}>
            <StackedHeading heading={copy.whyHeading} />
            <p>{copy.whyLead}</p>
            <RequirementText copy={copy} citeRequirement={flags.citeRequirement} />
            <ul>
              {copy.whyItems.map((item) => (
                <li key={item.title}>
                  <div className="icon" />
                  <h3>{item.title}</h3>
                  <p>{item.body}</p>
                </li>
              ))}
            </ul>
          </Col>
        </Row>

        {/* ── Section 4 — Most advanced ─────────────────────────────── */}
        <section className="landing-section-four reveal">
          <Row type="flex" justify="center">
            <Col xs={20} sm={20} md={22} lg={20} xl={18} xxl={16}>
              <StackedHeading heading={copy.recoverHeading} />
              <p>{copy.recoverLead}</p>
              <p>{copy.recoverClose}</p>
            </Col>
          </Row>
          <Row type="flex" className="landing-section-four-image">
            <Col xs={0} sm={0} md={12} lg={10} xl={12} xxl={12}>
              <div
                className="landing-banner"
                style={{
                  backgroundImage: `url(${images.screens})`,
                  height: '100%',
                }}
              />
            </Col>
            <Col
              className="landing-section-four-list"
              xs={{ span: 22, offset: 2, push: 1 }}
              sm={{ span: 21, offset: 3, push: 1 }}
              md={{ span: 10, offset: 1 }}
              lg={10}
              xl={8}
              xxl={7}
              justify="center"
              align="middle"
            >
              <ul>
                {copy.benefits.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </Col>
          </Row>
        </section>

        {/* ── Section 5 — CTA Banner ────────────────────────────────── */}
        <section className="landing-section-five" style={{ backgroundImage: `url(${images.bannerBottom})` }}>
          <div className="landing-section-five-overlay reveal">
            <StackedHeading heading={copy.ctaHeading} />
          </div>
        </section>

        {/* ── Section 6 — Top 10 Disruptions ───────────────────────── */}
        <Row type="flex" justify="center" className="landing-section-six reveal">
          <Col xs={20} sm={20} md={22} lg={20} xl={18} xxl={16}>
            <StackedHeading heading={copy.disruptionsHeading} />
            <div className="landing-disruption">
              <Button type="link" className="landing-disruption-btn" onClick={() => sliderRef.current.prev()}>
                <i className="fa fa-angle-left" />
              </Button>
              <div className="landing-disruption-carousel">
                <Carousel
                  dots={false}
                  ref={sliderRef}
                  slidesToShow={Math.min(Math.floor(innerWidth / 250), 5)}
                  slidesToScroll={Math.min(Math.floor(innerWidth / 250), 5)}
                >
                  {landing.disruptions.map((item) => (
                    <li key={item.rank} className={`landing-disruption-item ${item.tone}`}>
                      <i className={`fa ${item.icon} landing-disruption-item-icon`} />
                      <div className="landing-disruption-item-rank">
                        {item.rank}
                        <sup>{item.suffix}</sup>
                      </div>
                      <div className="landing-disruption-item-title">{item.title}</div>
                    </li>
                  ))}
                </Carousel>
              </div>
              <Button type="link" className="landing-disruption-btn" onClick={() => sliderRef.current.next()}>
                <i className="fa fa-angle-right" />
              </Button>
            </div>
            <p>{copy.disruptionsCaption}</p>
          </Col>
        </Row>

        {/* ── Section 7 — Latest News ───────────────────────────────── */}
        <Row type="flex" justify="center" className="landing-section-seven reveal">
          <Col xs={20} sm={20} md={22} lg={20} xl={18} xxl={16}>
            <div className="news-section">
              <div className="page-title">
                <h1>{copy.newsTitle}</h1>
              </div>
              <div className="landing-news-btn">
                <Button
                  type="link"
                  icon="left"
                  onClick={() => sliderNewsRef.current.prev()}
                  className="landing-news-btn-left"
                />
                <Button
                  type="link"
                  icon="right"
                  onClick={() => sliderNewsRef.current.next()}
                  className="landing-news-btn-right"
                />
              </div>
              <div className="news-container">
                <div className="carousel-content-container">
                  <Carousel
                    dots={false}
                    ref={sliderNewsRef}
                    slidesToShow={innerWidth <= 900 ? 1 : 4}
                    slidesToScroll={innerWidth <= 900 ? 1 : 2}
                  >
                    {newsList.map((news) => (
                      <NewsContainer
                        key={news.guid}
                        title={news.title}
                        content={news.content}
                        contentSnippet={news.contentSnippet}
                        creator={news.creator}
                        publishedDate={news.pubDate}
                        url={news.link}
                      />
                    ))}
                  </Carousel>
                </div>
              </div>
              <div className="btn-container">
                <Button type="primary" href={landing.newsUrl} size="large" target="_blank" rel="noopener noreferrer">
                  {copy.newsCta}
                </Button>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </Layout>
  );
}
